import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Clapperboard } from 'lucide-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { useNotifications } from '@/lib/NotificationContext';
import { api } from '@watchparty/shared';
import { bottomNavItems, adminNavItems, backToAppItem, notificationsNavItem } from './navItems';
function isActiveRoute(pathname, item) {
    if (item.exact)
        return pathname === item.to;
    if (item.to === '/')
        return pathname === '/';
    return pathname.startsWith(item.to);
}
export function BottomNav() {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const isAdmin = pathname.startsWith('/admin');
    const { user } = useAuth();
    const { unreadCount } = useNotifications();
    const { data: parties } = useQuery({
        queryKey: ['parties'],
        queryFn: () => api.get('/parties'),
        staleTime: Infinity,
        enabled: !!user,
    });
    const defaultPartyId = user?.defaultPartyId;
    const defaultParty = parties?.find((p) => p.partyId === defaultPartyId);
    const defaultPartyLabel = defaultParty?.name ?? 'My Party';
    const defaultPartyTo = defaultPartyId ? `/parties/${defaultPartyId}` : '/parties';
    const isDefaultPartyActive = !!defaultPartyId && pathname === `/parties/${defaultPartyId}`;
    if (isAdmin) {
        const items = [...adminNavItems, backToAppItem];
        return (_jsx("nav", { className: "bottom-nav", style: {
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
                background: 'var(--color-surface)',
                borderTop: '1px solid var(--color-border-strong)',
                backdropFilter: 'blur(8px)',
            }, children: _jsx("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${items.length}, 1fr)`,
                    gap: '4px',
                    padding: `8px 8px max(env(safe-area-inset-bottom), 6px)`,
                }, children: items.map((item) => {
                    const isActive = isActiveRoute(pathname, item);
                    const Icon = item.icon;
                    return (_jsxs(Link, { to: item.to, style: {
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: '4px',
                            minHeight: '56px', padding: '8px 4px',
                            fontSize: '0.7rem', fontWeight: 700,
                            fontFamily: 'var(--font-family-body)',
                            color: isActive ? 'var(--color-accent)' : 'var(--color-text-faint)',
                            transition: 'color 200ms',
                            textDecoration: 'none',
                            borderRadius: 'var(--radius-md)',
                        }, children: [_jsx(Icon, { size: 16 }), _jsx("span", { children: item.label })] }, item.to));
                }) }) }));
    }
    // Build items: Default Party + static items + Notifications
    const allItems = [
        { label: defaultPartyLabel, to: defaultPartyTo, icon: Clapperboard, isActive: isDefaultPartyActive, isNotif: false },
        ...bottomNavItems.map((item) => ({
            label: item.label, to: item.to, icon: item.icon,
            isActive: isActiveRoute(pathname, item), isNotif: false,
        })),
        {
            label: notificationsNavItem.label, to: notificationsNavItem.to, icon: notificationsNavItem.icon,
            isActive: isActiveRoute(pathname, notificationsNavItem), isNotif: true,
        },
    ];
    return (_jsx("nav", { className: "bottom-nav", style: {
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
            background: 'var(--color-surface)',
            borderTop: '1px solid var(--color-border-strong)',
            backdropFilter: 'blur(8px)',
        }, children: _jsx("div", { style: {
                display: 'grid',
                gridTemplateColumns: `repeat(${allItems.length}, 1fr)`,
                gap: '4px',
                padding: `8px 8px max(env(safe-area-inset-bottom), 6px)`,
            }, children: allItems.map((item) => {
                const Icon = item.icon;
                return (_jsxs(Link, { to: item.to, style: {
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: '4px',
                        minHeight: '56px', padding: '8px 4px',
                        fontSize: '0.7rem', fontWeight: 700,
                        fontFamily: 'var(--font-family-body)',
                        color: item.isActive ? 'var(--color-accent)' : 'var(--color-text-faint)',
                        transition: 'color 200ms',
                        textDecoration: 'none',
                        borderRadius: 'var(--radius-md)',
                    }, children: [_jsxs("span", { style: { position: 'relative' }, children: [_jsx(Icon, { size: 16 }), item.isNotif && unreadCount > 0 && (_jsx("span", { style: {
                                        position: 'absolute', top: '-6px', right: '-6px',
                                        minWidth: '12px', height: '12px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: 'var(--radius-full)',
                                        background: 'var(--color-text)',
                                        color: 'var(--color-bg)',
                                        fontSize: '8px', fontWeight: 700, lineHeight: 1, padding: '0 2px',
                                    }, children: unreadCount > 9 ? '9+' : unreadCount }))] }), _jsx("span", { style: {
                                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }, children: item.label })] }, item.to));
            }) }) }));
}
